@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.registrierung

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.example.project.utils.All
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.dom.Canvas
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement

private const val NETZWERK_MAP_CANVAS = "netzwerk-teilnehmer-map-canvas"
private const val NETZWERK_MAP_WIDTH = 760.0
private const val NETZWERK_MAP_HEIGHT = 1040.0

@Composable
fun NetzwerkTeilnehmerUebersicht(
    compact: Boolean = false,
    companies: List<All>? = null,
    mobileWide: Boolean = false
) {
    var loadedCompanies by remember { mutableStateOf<List<All>>(emptyList()) }
    LaunchedEffect(companies) {
        if (companies == null) loadedCompanies = DesktopRegistrierungApi.registeredCompanies()
    }
    val renderedCompanies = companies ?: loadedCompanies
    Canvas(attrs = {
        id(NETZWERK_MAP_CANVAS)
        attr("width", NETZWERK_MAP_WIDTH.toInt().toString())
        attr("height", NETZWERK_MAP_HEIGHT.toInt().toString())
        classes(NetzwerkLoginStyles.bgGif)
        style {
            if (mobileWide) {
                property("width", "90vw")
                property("max-width", "90vw")
                property("height", "auto")
            } else {
                property("width", "auto")
                maxWidth(100.percent)
                height(if (compact) 45.vh else 75.vh)
            }
            property("aspect-ratio", "${NETZWERK_MAP_WIDTH.toInt()} / ${NETZWERK_MAP_HEIGHT.toInt()}")
        }
    })

    LaunchedEffect(renderedCompanies, compact) {
        renderNetzwerkTeilnehmerMap(renderedCompanies)
    }
}

private suspend fun renderNetzwerkTeilnehmerMap(companies: List<All>) {
    val canvas = document.getElementById(NETZWERK_MAP_CANVAS) as? HTMLCanvasElement ?: return
    val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return
    val germanyGeoJson = window.fetch("mapdata/deutschland.geo.json").await().json().await()
    val statesGeoJson = window.fetch("mapdata/bundeslaender.geo.json").await().json().await()
    val plzCsv = window.fetch("mapdata/plz_geocoord.csv").await().text().await()
    val plzIndex = parseNetzwerkPlzCoordinates(plzCsv)

    ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
    drawNetzwerkGeoJsonPolygons(ctx, germanyGeoJson, fill = "rgba(255,255,255,0.018)", stroke = "rgba(255,255,255,0.88)", strokeWidth = 1.7)
    drawNetzwerkGeoJsonPolygons(ctx, statesGeoJson, fill = null, stroke = "rgba(255,255,255,0.88)", strokeWidth = 1.0)
    drawNetzwerkStateLabels(ctx)
    companies
        .filter { it.plz.trim().length == 5 }
        .forEach { company ->
            val coordinate = plzIndex[company.plz.trim()] ?: return@forEach
            val isLumatica = company.firma.contains("lumatica", ignoreCase = true) ||
                company.kunde.contains("lumatica", ignoreCase = true) ||
                company.firmenId.equals("ls1000", ignoreCase = true)
            drawNetzwerkCompanyPoint(ctx, coordinate.first, coordinate.second, isLumatica)
        }
}

private fun parseNetzwerkPlzCoordinates(csv: String): Map<String, Pair<Double, Double>> {
    val result = mutableMapOf<String, Pair<Double, Double>>()
    csv.lineSequence().drop(1).forEach { line ->
        val cells = line.split(",")
        if (cells.size < 3) return@forEach
        val plz = cells[0].trim().padStart(5, '0').take(5)
        val lat = cells[1].trim().toDoubleOrNull()
        val lon = cells[2].trim().toDoubleOrNull()
        if (plz.length == 5 && lat != null && lon != null) result[plz] = lat to lon
    }
    return result
}

private fun drawNetzwerkGeoJsonPolygons(ctx: CanvasRenderingContext2D, geoJson: dynamic, fill: String?, stroke: String, strokeWidth: Double) {
    val features = geoJson.features ?: return
    val featureCount = features.length as Int
    for (featureIndex in 0 until featureCount) {
        val geometry = features[featureIndex].geometry ?: continue
        val type = geometry.type as String
        val coordinates = geometry.coordinates
        if (type == "Polygon") {
            drawNetzwerkPolygon(ctx, coordinates, fill, stroke, strokeWidth)
        } else if (type == "MultiPolygon") {
            val polygonCount = coordinates.length as Int
            for (polygonIndex in 0 until polygonCount) {
                drawNetzwerkPolygon(ctx, coordinates[polygonIndex], fill, stroke, strokeWidth)
            }
        }
    }
}

private fun drawNetzwerkPolygon(ctx: CanvasRenderingContext2D, polygon: dynamic, fill: String?, stroke: String, strokeWidth: Double) {
    val ringCount = polygon.length as Int
    for (ringIndex in 0 until ringCount) {
        val ring = polygon[ringIndex]
        val pointCount = ring.length as Int
        if (pointCount == 0) continue
        ctx.beginPath()
        for (pointIndex in 0 until pointCount) {
            val point = projectNetzwerkGermanyPoint(
                lat = (ring[pointIndex][1] as Number).toDouble(),
                lon = (ring[pointIndex][0] as Number).toDouble()
            )
            if (pointIndex == 0) ctx.moveTo(point.first, point.second) else ctx.lineTo(point.first, point.second)
        }
        ctx.closePath()
        if (fill != null && ringIndex == 0) {
            ctx.fillStyle = fill
            ctx.fill()
        }
        ctx.save()
        ctx.shadowColor = "rgba(255,255,255,0.28)"
        ctx.shadowBlur = 8.0
        ctx.strokeStyle = stroke
        ctx.lineWidth = strokeWidth
        ctx.stroke()
        ctx.restore()
    }
}

private fun projectNetzwerkGermanyPoint(lat: Double, lon: Double): Pair<Double, Double> {
    val minLat = 47.05
    val maxLat = 55.15
    val minLon = 5.85
    val maxLon = 15.05
    val x = ((lon - minLon) / (maxLon - minLon)) * NETZWERK_MAP_WIDTH
    val y = (1.0 - ((lat - minLat) / (maxLat - minLat))) * NETZWERK_MAP_HEIGHT
    return x to y
}

private fun drawNetzwerkCompanyPoint(ctx: CanvasRenderingContext2D, lat: Double, lon: Double, lumatica: Boolean) {
    val point = projectNetzwerkGermanyPoint(lat, lon)
    val radius = if (lumatica) 5.4 else 5.0
    val color = if (lumatica) "255,0,0" else "0,224,255"
    val glow = ctx.createRadialGradient(point.first, point.second, 0.0, point.first, point.second, radius * 2.2)
    glow.addColorStop(0.0, "rgba(255,255,255,0.82)")
    glow.addColorStop(0.24, "rgba($color,0.50)")
    glow.addColorStop(0.62, "rgba($color,0.12)")
    glow.addColorStop(1.0, "rgba($color,0)")
    ctx.fillStyle = glow
    ctx.beginPath()
    ctx.arc(point.first, point.second, radius * 2.2, 0.0, kotlin.math.PI * 2)
    ctx.fill()
    ctx.fillStyle = "rgba(255,255,255,0.88)"
    ctx.beginPath()
    ctx.arc(point.first, point.second, if (lumatica) 3.0 else 2.8, 0.0, kotlin.math.PI * 2)
    ctx.fill()
}

private fun drawNetzwerkStateLabels(ctx: CanvasRenderingContext2D) {
    val labels = listOf(
        Triple("SH", 54.18, 9.86), Triple("HH", 53.55, 10.00), Triple("MV", 53.62, 12.45),
        Triple("HB", 53.08, 8.80), Triple("NI", 52.72, 9.55), Triple("BB", 52.36, 13.30),
        Triple("BE", 52.52, 13.40), Triple("ST", 51.95, 11.68), Triple("NRW", 51.43, 7.55),
        Triple("HE", 50.65, 9.02), Triple("TH", 50.90, 11.02), Triple("SN", 51.02, 13.35),
        Triple("RLP", 49.95, 7.45), Triple("SL", 49.38, 7.00), Triple("BW", 48.55, 9.02),
        Triple("BY", 48.80, 11.55)
    )
    ctx.fillStyle = "rgba(247,247,245,0.72)"
    ctx.font = "700 15px system-ui, Segoe UI, Arial"
    ctx.asDynamic().textAlign = "center"
    ctx.asDynamic().textBaseline = "middle"
    labels.forEach { (label, lat, lon) ->
        val point = projectNetzwerkGermanyPoint(lat, lon)
        ctx.fillText(label, point.first, point.second)
    }
}
